import * as Yup from 'yup';
import psl from 'psl';
import { useFormik } from 'formik';
import { useState } from 'react';
import { useHistory, useRouteMatch, useRouterHelper, useTranslation } from '@shared/core';
import { useMediaQuery } from '@withjoy/joykit/utils';
import { extractHostname } from '@shared/utils/extractHostname';
import { HOW_IT_WORS_SECTION_ID } from '../../FriendsRegistry.constants';
import { useFieldErrors } from '@apps/createWedding/hooks/useFieldErrors';
import { useFriendsRegistryTelemetry } from '../../FriendsRegistry.telemetry';
import { CreateTransitionalRegistryResponse, useCreateTransitionalRegistry } from '../../hooks/useCreateTransitionalRegistry';
import { TransitionalRegistryStatus, useCheckTransitionalRegistryUrlLazyQuery } from '@graphql/generated';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { useFriendsRegistryRoutePaths } from '../../FriendsRegistry.routes';
import { FriendsRegistryTelemetryPageName } from './AddFromFriendsRegistryBanner';

const VIDEO_URL = 'https://player.vimeo.com/video/885396263?badge=0&amp;autopause=0&amp;quality_selector=1&amp;player_id=0&amp;app_id=58479&background=1';
const VIDEO_THUMBNAIL_URL = `https://withjoy.com/assets/public/shop/features/add-from-registry-url/Joy_CopyFriend'sRegistry_v3_600x680_placeholder.png`;

const MOBILE_VIDEO_URL = 'https://player.vimeo.com/video/885396251?badge=0&amp;autopause=0&amp;quality_selector=1&amp;player_id=0&amp;app_id=58479&background=1';
const MOBILE_VIDEO_THUMBNAIL_URL = `https://withjoy.com/assets/public/shop/features/add-from-registry-url/Joy_CopyFriend'sRegistry_v3_390x390_placeholder.png`;

export type AddFromFriendsRegistryFormProps = {
  registryUrl: string;
};

export const useAddFromFriendsRegistryBannerController = (eventId: string, telemetryPageName: FriendsRegistryTelemetryPageName, onHowItWorksClick?: () => void) => {
  const history = useHistory();
  const routeHelpers = useRouterHelper();
  const { friendsRegistryRoutes } = useFriendsRegistryRoutePaths();
  const telemetry = useFriendsRegistryTelemetry();
  const { t2 } = useTranslation('addFromFriendsRegistry');
  const translations = t2('banner');
  const isMobile = useMediaQuery(theme => theme.mediaQueries.between(0, { breakpointAlias: 'sm2' }));
  const [isScannerDialogOpen, setIsScannerDialogOpen] = useState(false);
  const [registryInfo, setRegistryInfo] = useState<{ id: string; status: TransitionalRegistryStatus; dateString?: string }>({
    id: '',
    status: TransitionalRegistryStatus.inProgress
  });

  const onCreateRegistry = (createTransitionalRegistry: CreateTransitionalRegistryResponse) => {
    setRegistryInfo({ id: createTransitionalRegistry.id ?? '', status: createTransitionalRegistry.status, dateString: createTransitionalRegistry.scannedAt?.dateString });
    if (createTransitionalRegistry.id) {
      setIsScannerDialogOpen(true);
      formik.resetForm();
      if (TransitionalRegistryStatus.failed === createTransitionalRegistry.status) {
        setIsScannerDialogOpen(false);
      }
    }
  };

  const onError = (message?: string) => formik.setFieldError('registryUrl', message);

  const { createTransitionalRegistry, loading, data } = useCreateTransitionalRegistry({ eventId, telemetryPageName, onCreateRegistry, onError });

  const [checkUrlRequest, { loading: loadingCheckUrl }] = useCheckTransitionalRegistryUrlLazyQuery({
    batchMode: 'fast',
    onCompleted: data => {
      if (data?.checkTransitionalRegistryUrl && 'canonicalUrl' in data?.checkTransitionalRegistryUrl) {
        createTransitionalRegistry(data.checkTransitionalRegistryUrl.canonicalUrl);
      }
      if (data?.checkTransitionalRegistryUrl && 'reasonCode' in data?.checkTransitionalRegistryUrl) {
        formik.setFieldError('registryUrl', translations.errors.reasonCode[data?.checkTransitionalRegistryUrl?.reasonCode] ?? translations.errors.reasonCode.registryUnavailable);
      }
    },
    onError: error => {
      formik.setFieldError('registryUrl', translations.errors.reasonCode.registryUnavailable);
    }
  });

  const videoUrl = isMobile ? MOBILE_VIDEO_URL : VIDEO_URL;
  const videoThumbnailUrl = isMobile ? MOBILE_VIDEO_THUMBNAIL_URL : VIDEO_THUMBNAIL_URL;

  const formik = useFormik<AddFromFriendsRegistryFormProps>({
    initialValues: { registryUrl: '' },
    validationSchema: Yup.object<AddFromFriendsRegistryFormProps>({
      registryUrl: Yup.string()
        .required(translations.errors.registryUrlRequired)
        .test('is-valid-domain', translations.errors.registryUrlInvalid, value => {
          if (value) {
            const newDomain = psl.get(extractHostname(value));
            return newDomain ? psl.isValid(newDomain) : false;
          }
          return false;
        })
    }),
    validateOnChange: true,
    validateOnBlur: false,
    onSubmit: values => checkUrlRequest({ variables: { url: values.registryUrl } })
  });

  const getFieldErrorProps = useFieldErrors<AddFromFriendsRegistryFormProps>(formik);

  const isFriendsRegistryRouteMatch = useRouteMatch({ path: routeHelpers.buildPath(friendsRegistryRoutes.addToFriendsRegistryIndex.path) });

  const handleHowItWorks = useEventCallback(() => {
    telemetry.registryUrlInfo(telemetryPageName, eventId);
    isFriendsRegistryRouteMatch
      ? onHowItWorksClick?.()
      : history.push({ pathname: routeHelpers.buildPath(friendsRegistryRoutes.addToFriendsRegistryIndex.path), search: `?scrollto=${HOW_IT_WORS_SECTION_ID}` });
  });

  const handleOnCloseDialog = () => {
    setIsScannerDialogOpen(false);
    setRegistryInfo({ id: '', status: TransitionalRegistryStatus.inProgress });
  };

  return {
    formik,
    telemetry,
    loading: loadingCheckUrl || loading,
    data,
    translations,
    videoUrl,
    videoThumbnailUrl,
    isScannerDialogOpen,
    registryInfo,
    getFieldErrorProps,
    handleHowItWorks,
    handleOnCloseDialog
  };
};
